/**
 * Slack API를 사용하여 사용자 정보를 가져오는 유틸리티
 */

export interface SlackUser {
    id: string;
    name: string;
    real_name: string;
    display_name: string;
    deleted?: boolean;
    is_bot?: boolean;
    profile: {
        first_name: string;
        image_72?: string;
        image_192?: string;
        image_512?: string;
        image_original?: string;
    };
}

export interface SlackUsersResponse {
    ok: boolean;
    members: SlackUser[];
    error?: string;
}

/**
 * 서버 API를 통해 Slack 사용자 목록을 가져옵니다
 * @param apiBaseUrl 서버 API 기본 URL (선택사항, 기본값: '/api')
 * @returns 사용자 목록
 */
export async function fetchSlackUsers(apiBaseUrl: string = '/api'): Promise<SlackUser[]> {
    try {
        const response = await fetch(`${apiBaseUrl}/slack/users`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }

        const data: SlackUsersResponse = await response.json();

        if (!data.ok) {
            throw new Error(data.error || 'Failed to fetch Slack users');
        }

        return data.members || [];
    } catch (error) {
        console.error('Error fetching Slack users:', error);
        throw error;
    }
}

/**
 * 사용자 이름을 Slack 사용자와 매칭합니다
 * 여러 필드를 확인하여 가장 일치하는 사용자를 찾습니다
 * @param name 매칭할 이름
 * @param slackUsers Slack 사용자 목록
 * @returns 매칭된 Slack 사용자 또는 null
 */
export function matchSlackUser(
    name: string,
    slackUsers: SlackUser[]
): SlackUser | null {
    const normalizedName = name.trim().toLowerCase();

    // 정확한 매칭 우선
    let matched = slackUsers.find(
        (user) =>
            user.name.toLowerCase() === normalizedName ||
            user.real_name.toLowerCase() === normalizedName
    );

    if (matched) return matched;

    // 부분 매칭 시도
    matched = slackUsers.find(
        (user) =>
            user.name.toLowerCase().includes(normalizedName) ||
            user.real_name.toLowerCase().includes(normalizedName) ||
            normalizedName.includes(user.name.toLowerCase()) ||
            normalizedName.includes(user.real_name.toLowerCase())
    );

    return matched || null;
}

/**
 * Slack 프로필 이미지 URL을 가져옵니다
 * @param user Slack 사용자
 * @param size 이미지 크기 (72, 192, 512, original)
 * @returns 이미지 URL
 */
export function getSlackImageUrl(
    user: SlackUser,
    size: '72' | '192' | '512' | 'original' = '192'
): string | null {
    const profile = user.profile;

    switch (size) {
        case 'original':
            return profile.image_original || profile.image_512 || profile.image_192 || profile.image_72 || null;
        case '512':
            return profile.image_512 || profile.image_192 || profile.image_72 || null;
        case '192':
            return profile.image_192 || profile.image_72 || null;
        case '72':
            return profile.image_72 || null;
        default:
            return profile.image_192 || profile.image_72 || null;
    }
}

